// Twilio SMS block error codes and their user-friendly messages
// Reference: https://www.twilio.com/docs/api/errors

const TWILIO_ERROR_MESSAGES: Record<string, string> = {
    '21211':
        'Invalid phone number - the number may not support SMS or voice calls',
    '21408':
        'SMS permission not enabled for this region - carrier restrictions apply',
    '21610':
        'Recipient has opted out of messages - they replied STOP and must reply START to receive messages again',
    '21614': 'Destination is not a valid mobile number',
    '30003':
        'Unreachable phone - the device may be powered off, in airplane mode, or out of signal range',
    '30004': 'Carrier blocked this message',
    '30005':
        'Could not reach this phone - the number may be inactive or temporarily unavailable',
    '30006': 'Landline or unreachable carrier - the number cannot receive SMS',
    '30007': 'Carrier filtered or blocked this message',
    '30008': 'Unknown delivery error reported by the carrier',
    '30019': 'Outbound message exceeded carrier size limit',
    '30032': "Sender toll-free number isn't verified for this carrier",
    '11200': 'Provider returned a delivery error',
    '60005': 'Provider returned a delivery error',
};

// Short, inline-friendly labels for each Twilio error code. Intended for
// compact UI surfaces (e.g. the "Send failed — …" line on a message bubble)
// where the full reason is available on hover.
const TWILIO_ERROR_SHORT_MESSAGES: Record<string, string> = {
    '21211': 'Invalid number',
    '21408': 'Region not permitted',
    '21610': 'Recipient opted out',
    '21614': 'Not a mobile number',
    '30003': 'Phone unreachable',
    '30004': 'Carrier blocked',
    '30005': 'Phone unavailable',
    '30006': 'Landline or unreachable carrier',
    '30007': 'Carrier blocked',
    '30008': 'Delivery error',
    '30019': 'Message too long',
    '30032': 'Toll-free unverified',
    '11200': 'Delivery error',
    '60005': 'Delivery error',
};

// Soft block codes - temporary issues that don't prevent message delivery attempts
const SOFT_BLOCK_CODES = new Set(['30003', '30005']);

// Failures where something refused the message rather than mishandled it: the
// carrier filtered it, the region forbids it, or the caregiver replied STOP.
// These are the ones a coordinator has to act on out-of-band -- retrying will
// not help -- which is why they are separated from ordinary send failures.
const RECIPIENT_BLOCK_CODES = new Set(['21408', '21610', '30004', '30007']);

/**
 * Whether a failure means the recipient or their carrier refused the message,
 * as opposed to a delivery error that may succeed on a later send.
 */
export function isRecipientBlockCode(errorCode: string): boolean {
    return RECIPIENT_BLOCK_CODES.has(errorCode);
}

const DEFAULT_ERROR_MESSAGE = 'Unable to reach this phone number';
const DEFAULT_SHORT_ERROR_MESSAGE = 'Undeliverable';

/**
 * Returns whether an error code represents a soft block (temporary issue).
 * Soft blocks show a warning but still allow message delivery attempts.
 */
export function isSoftBlockCode(errorCode: string): boolean {
    return SOFT_BLOCK_CODES.has(errorCode);
}

/**
 * Returns a user-friendly error message for a blocked phone error code.
 * Currently supports Twilio error codes.
 */
export function getBlockedPhoneErrorMessage(
    errorCode: string,
    provider: string,
): string {
    if (provider === 'twilio') {
        return TWILIO_ERROR_MESSAGES[errorCode] ?? DEFAULT_ERROR_MESSAGE;
    }
    return DEFAULT_ERROR_MESSAGE;
}

/**
 * Returns a short, inline-friendly label for a blocked phone error code,
 * suitable for compact UI (pair with getBlockedPhoneErrorMessage in a tooltip).
 */
export function getBlockedPhoneShortMessage(
    errorCode: string,
    provider: string,
): string {
    if (provider === 'twilio') {
        return (
            TWILIO_ERROR_SHORT_MESSAGES[errorCode] ??
            DEFAULT_SHORT_ERROR_MESSAGE
        );
    }
    return DEFAULT_SHORT_ERROR_MESSAGE;
}
